import Dexie, { type Table } from 'dexie'
import type { Booking } from '@/lib/types/booking'
import type { Area } from '@/lib/types/area'
import type { Table as RestaurantTable } from '@/lib/types/table'
import type { OpeningHour } from '@/lib/types/openingHour'
import type { CustomField } from '@/lib/types/customField'
import type { MenuCategory, MenuItem, BookingPreOrderItem, POSOrder } from '@/lib/types/pos'
import type { CustomerOrder } from '@/lib/types/customerOrder'

// ---------------------------------------------------------------------------
// QueuedOp — defined here to avoid circular imports with QueueManager
// ---------------------------------------------------------------------------
export type HttpMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface QueuedOp {
  id: string
  method: HttpMethod
  path: string
  body?: unknown
  createdAt: number
  retries: number
  /** Present on POST ops — the 'temp-...' documentId for identity tracking */
  tempId?: string
  /** Resolved real documentIds from earlier ops in this session, keyed by tempId */
  resolvedIds?: Record<string, string>
}

// ---------------------------------------------------------------------------
// AppDatabase — one Dexie table per entity + syncQueue
//
// NOTE: "tables" is a reserved property on the Dexie base class, so we use
// "floorTables" for the restaurant tables entity store.
// ---------------------------------------------------------------------------
export class AppDatabase extends Dexie {
  bookings!: Table<Booking, string>
  customerOrders!: Table<CustomerOrder, string>
  areas!: Table<Area, string>
  floorTables!: Table<RestaurantTable, string>   // "tables" is reserved by Dexie
  openingHours!: Table<OpeningHour, string>
  customFields!: Table<CustomField, string>
  menuCategories!: Table<MenuCategory, string>
  menuItems!: Table<MenuItem, string>
  preOrderItems!: Table<BookingPreOrderItem, string>
  posOrders!: Table<POSOrder, string>
  syncQueue!: Table<QueuedOp, string>

  constructor() {
    super('tablesmanagement')

    // v1 used "tables" store — drop it and replace with "floorTables" in v2
    this.version(1).stores({
      bookings: 'documentId, date, status',
      areas: 'documentId',
      tables: 'documentId',
      openingHours: 'documentId',
      customFields: 'documentId',
      syncQueue: 'id, createdAt',
    })

    this.version(2).stores({
      tables: null,          // drop the conflicting store
      floorTables: 'documentId',
    })

    // v3: add menu stores for offline POS menu access
    this.version(3).stores({
      menuCategories: 'documentId',
      menuItems: 'documentId',
    })

    // v4: pre-order items for offline booking pre-orders
    this.version(4).stores({
      preOrderItems: 'documentId, bookingDocumentId',
    })

    // v5: POS orders (denormalized — embeds orderItems + payments) for offline POS
    this.version(5).stores({
      posOrders: 'documentId, openedAt, tableDocId',
    })

    this.version(6).stores({
      customerOrders: 'documentId, date, status',
      preOrderItems: 'documentId, bookingDocumentId, orderDocumentId',
    })
  }
}

export const db = new AppDatabase()

/** Clear all entity data (call on logout to prevent data leakage between accounts) */
export async function clearDb(): Promise<void> {
  await Promise.all([
    db.bookings.clear(),
    db.customerOrders.clear(),
    db.areas.clear(),
    db.floorTables.clear(),
    db.openingHours.clear(),
    db.customFields.clear(),
    db.menuCategories.clear(),
    db.menuItems.clear(),
    db.preOrderItems.clear(),
    db.posOrders.clear(),
    db.syncQueue.clear(),
  ])
}
